import { ChangeDetectionStrategy, Component, HostListener, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';

import { CreateRelationDialogOutput } from '@common/entities/dialog/create-relation-dialog.entity';
import { FragmentsSandbox } from '@common/modules/records/sandbox/fragments.sandbox';

import {
    ApiFragment,
    BaseContainer,
    DialogConfig,
    DialogRef,
    FragmentId,
    MultiFormSelectOptions
} from '@stylehaven/components';
@Component({
    selector: 'mhv-create-relation-dialog',
    templateUrl: './create-relation-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateRelationDialogComponent extends BaseContainer implements OnInit {
    private readonly searchTerm$: Subject<string> = new Subject<string>();

    public relationTypeFormSelectOptions$: Observable<MultiFormSelectOptions>;
    public fragmentsFormSelectOptions$: Observable<MultiFormSelectOptions<ApiFragment>>;

    public createRelationFormGroup: UntypedFormGroup;
    public fragmentsLabel: string;
    public fragmentsSearchPlaceholder: string;

    constructor(
        private readonly sandbox: FragmentsSandbox,
        private readonly formBuilder: UntypedFormBuilder,
        public config: DialogConfig,
        public dialog: DialogRef<CreateRelationDialogOutput>
    ) {
        super();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(_event: { target: HTMLInputElement }) {
        // Prevent submitting when form is invalid
        if (!this.createRelationFormGroup.valid) {
            return;
        }
        this.submit();
    }

    ngOnInit(): void {
        this.initLabels();
        this.initForm();
        this.initRelationTypesOptionsStream();
        this.initFragmentsStream();
    }

    private initLabels(): void {
        this.fragmentsLabel = this.getFragmentsLabel();
        this.fragmentsSearchPlaceholder = this.getFragmentsSearchPlaceholder();
    }

    private initForm(): void {
        this.createRelationFormGroup = this.formBuilder.group({
            relationTypes: [null, Validators.required],
            fragments: [null, Validators.required]
        });
    }

    private initRelationTypesOptionsStream(): void {
        this.relationTypeFormSelectOptions$ = this.sandbox.getNonSystemRelationTypesOptions();
    }

    private initFragmentsStream(): void {
        this.fragmentsFormSelectOptions$ = this.searchTerm$.pipe(
            distinctUntilChanged(),
            debounceTime(500),
            startWith(''),
            switchMap(searchTerm =>
                this.isCollection()
                    ? this.sandbox.getCollectionsAsFormSelectOptions(searchTerm, this.sourceFragmentId)
                    : this.sandbox.getFragmentsAsFormSelectOptions(searchTerm, this.sourceFragmentId)
            )
        );
    }

    public onSearchTermChange(searchTerm: string): void {
        this.searchTerm$.next(searchTerm);
    }

    submit(): void {
        const relationTypesValues: string[] = this.relationTypesControl.value;
        const fragmentsValues: ApiFragment[] = this.fragmentsControl.value;

        if (relationTypesValues.length === 0 || fragmentsValues.length === 0) {
            console.error('CreateRelationDialog', 'No value selected');
            return;
        }

        const relationType: string = relationTypesValues[0];
        const fragmentId: FragmentId = fragmentsValues[0].getFragmentId();

        const output: CreateRelationDialogOutput = {
            type: 'success',
            relationType,
            fragmentId
        };
        this.dialog.close(output);
    }

    cancel(): void {
        const output: CreateRelationDialogOutput = { type: 'cancel' };
        this.dialog.close(output);
    }

    private getFragmentsLabel(): string {
        return this.isCollection()
            ? 'items.create-relation-dialog.collection-selection-label'
            : 'items.create-relation-dialog.item-selection-label';
    }

    private getFragmentsSearchPlaceholder(): string {
        return this.isCollection()
            ? 'items.create-relation-dialog.search-collection'
            : 'items.create-relation-dialog.search-item';
    }

    isCollection(): boolean {
        return this.config.data.isCollection;
    }

    get sourceFragmentId(): FragmentId {
        return this.config.data.fragmentId;
    }

    get relationTypesControl(): UntypedFormControl {
        return this.createRelationFormGroup.get('relationTypes') as UntypedFormControl;
    }

    get fragmentsControl(): UntypedFormControl {
        return this.createRelationFormGroup.get('fragments') as UntypedFormControl;
    }
}
